import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { map, Observable, tap, throwError } from 'rxjs';
import { PaginationModel } from '@core/models/Pagination';
import { HttpClientService } from '@core/services/http-client.service';
import { IntervuBookingDto } from '@core/models/dto/IntervuBookingModelDto';
import { VisionBookingDto } from '@core/models/dto/VisionBookingModelDto';
import { TemplatesDto } from '@core/models/dto/TemplatesDto';
import { BookingsType } from '@core/enums/BookingsType.enum';
import { BookingsDto } from '@core/models/dto/BookingsDto';
import { PageSize } from '@core/enums/PageSize.enum';
import { ProjectType } from '@core/enums/ProjectType.enum';
import { JsonFormatterService } from '@booking/common/json-formatter.service';
import { MessageType } from '@core/enums/MessageType.enum';
import { MessageService } from '@core/services/message.service';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private httpClientService = inject(HttpClientService);
  private jsonFormmater = inject(JsonFormatterService);
  private messageService = inject(MessageService);

  getBookings(type: BookingsType, pageSize = PageSize.DEFAULT): Observable<BookingsDto[]> {
    const params = new HttpParams().set('pageNumber', 1).set('pageSize', pageSize);
    return this.httpClientService.get<PaginationModel<BookingsDto>>(`BookingDashboard/${type}`, params).pipe(
      map(res => res.items)
    );
  }

  cancelBooking(researchId: number) {
    return this.httpClientService.post(null, `BookingDashboard/CancelResearch/${researchId}`)
    .pipe(tap(() => this.messageService.loadMessage('Booking canceled.', MessageType.SAVED)));
  }

  downloadPdf(researchId: number, projectType: ProjectType): Observable<Blob> {
    let download$;
    switch (projectType) {
      case ProjectType.VISION:
        download$ = this.httpClientService.downloadFile(`BookingDashboard/FacilityResearches/DownLoad/${researchId}`);
        break;
      case ProjectType.INTERVU:
        download$ =  this.httpClientService.downloadFile(`BookingDashboard/OnlineResearches/DownLoad/${researchId}`);
        break;
      case ProjectType.VISIONRAQ:
        download$ =  this.httpClientService.downloadFile(`RequestAQuote/FacilityRequestAQuote/DownLoad/${researchId}`);
        break;
      case ProjectType.INTERVURAQ:
        download$ =  this.httpClientService.downloadFile(`RequestAQuote/OnlineRequestAQuote/DownLoad/${researchId}`);
        break;
      default:
        return throwError(() => 'Unknown project type')
    }
    return download$.pipe(
      tap(blob => this.openPdfInNewWindow(blob))
    )
  }

  private openPdfInNewWindow(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.addEventListener('beforeunload', () => {
        URL.revokeObjectURL(url);
      });
    }
  }

  getIntervuBooking(researchId: number) {
    return this.httpClientService.get<IntervuBookingDto>(`BookingDashboard/OnlineResearches/${researchId}`);
  }

  getFacilityBooking(researchId: number) {
    return this.httpClientService.get<VisionBookingDto>(`BookingDashboard/FacilityResearches/${researchId}`);
  }

  getTemplate(templateId:string): Observable<TemplatesDto>{
    return this.httpClientService.get(`BookingDashboard/templates/${templateId}`);
  }

  getTemplates(): Observable<TemplatesDto[]>{
    return this.httpClientService.get(`BookingDashboard/templates`);
  }

  saveTemplate(template: TemplatesDto){
    const sanatizedTemplate = this.removeOptionalFields(template);
    return this.httpClientService.post(sanatizedTemplate, 'BookingDashboard/templates')
    .pipe(tap(() => this.messageService.loadMessage('Template saved.', MessageType.SAVED)));
  }

  editTemplate(template:TemplatesDto, templateId:string ){
    const sanatizedTemplate = this.removeOptionalFields(template);
    return this.httpClientService.put(sanatizedTemplate, `BookingDashboard/templates/${templateId}`)
      .pipe(tap(() => this.messageService.loadMessage('Template changed.', MessageType.SAVED)));
  }

  removeOptionalFields(template:TemplatesDto): TemplatesDto{
    const processedData = { ...template };
    const { billingDetail } = processedData;
    if (billingDetail.poNumberNotRequired) {
      delete billingDetail.poNumber;
    }
    if (billingDetail.jobNumberNotRequired) {
      delete billingDetail.jobNumber;
    }
    return this.jsonFormmater.cleanData(processedData);
  }

  deleteTemplate(templateId: number) {
    return this.httpClientService.delete(`BookingDashboard/templates/${templateId}`)
      .pipe(tap(() => this.messageService.loadMessage('Template deleted.', MessageType.DELETED)));
  }
}
